import {Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {SharedModule} from "../../../shared/shared.module";
import {ServiceFormService} from "../../service/service-form.service";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatError, MatFormField, MatHint, MatLabel, MatSuffix} from "@angular/material/form-field";
import {PayService} from "../../../shared/service/pay/pay.service";
import {AsyncPipe} from "@angular/common";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {MatInput} from "@angular/material/input";
import {GeneralLedgerService} from "../../../general-ledger/service/general-ledger.service";
import {MatAutocomplete, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {MatIcon} from "@angular/material/icon";
import {VendorModel, VendorService} from "../../../vendor/service/vendor.service";
import {Observable} from "rxjs";
import {HttpParams} from "@angular/common/http";
import {ServiceFormValues} from "../../model/service-form";
import {autocompleteFormFilter, replayHttpQuery} from "../../../../infrastructure/helper/autocomplete-form-filter";
import {Agent} from "../../../agent/component/service/agent.model";
import {AgentService} from "../../../agent/component/service/agent.service";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {AdxInvoiceDetailResponse} from "../../../adx-invoice/model/adx-invoice-detail-response";
import {AdxInvoiceService} from "../../../adx-invoice/service/adx-invoice.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatSnackBar} from "@angular/material/snack-bar";
import {combineLatest} from "rxjs";
import {startWith} from "rxjs/operators";

@Component({
    selector: 'app-create-service-dialog',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        AsyncPipe,
        SharedModule,
        MatButton,
        MatFormField,
        MatError,
        MatSelect,
        MatOption,
        MatLabel,
        MatInput,
        MatAutocomplete,
        MatAutocompleteTrigger,
        MatIcon,
        MatIconButton,
        MatSuffix,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatHint,
        MatProgressSpinner,
    ],
    templateUrl: './create-service-dialog.component.html',
    styleUrl: './create-service-dialog.component.scss'
})
export class CreateServiceDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly dialogRef = inject(MatDialogRef<CreateServiceDialogComponent>);

  private readonly data = inject<{ adxInvoiceId: string }>(MAT_DIALOG_DATA);

  private readonly snackBar = inject(MatSnackBar);

  private readonly adxInvoiceService = inject(AdxInvoiceService);

  private readonly serviceForm = inject(ServiceFormService);

  private readonly payService = inject(PayService);

  private readonly generalLedgerService = inject(GeneralLedgerService);

  private readonly vendorService = inject(VendorService);

  private readonly agentService = inject(AgentService);

  protected readonly form = this.serviceForm.form;

  protected readonly submitTo$ = this.payService.getSubmitTo();

  protected readonly travelType$ = this.generalLedgerService.getTravelTypeList();

  protected readonly externalTravelServiceType$ = this.generalLedgerService.getExternalTravelServiceTypeList();

  protected readonly currencies$ = this.payService.getCurrency({all: true});

  protected readonly indicators = computed(() => ([
    {
      displayName: 'N/A',
      value: '-1',
    },
    {
      displayName: 'Domestic',
      value: 'D',
    },
    {
      displayName: 'International',
      value: 'I',
    },
    {
      displayName: 'Transborder',
      value: 'T',
    }
  ]));

  protected readonly loading = signal(false);

  protected get departDateControl() {
    return this.form.controls.departDate;
  }

  protected get returnDateControl() {
    return this.form.controls.returnDate;
  }

  protected get vendorControl() {
    return this.form.controls.vendor;
  }

  protected get agentControl() {
    return this.form.controls.agent;
  }

  protected filteredVendorOptions$: Observable<VendorModel[]> | null = null;

  protected filteredAgentOptions$: Observable<Agent[]> | null = null;

  protected returnDateFilter = (d: Date | null): boolean => {
    const departDate = this.departDateControl.value;
    if (!departDate) return true;
    return (d || new Date()) >= new Date(departDate);
  };

  public ngOnInit() {
    this.subscribeToControls();
    this.getAdxInvoiceInfo();
    this.form.get('externalTravelServiceTypeId')?.disable();
  }

  private getAdxInvoiceInfo() {
    this.adxInvoiceService
      .searchByQueryAdxInvoices({id: this.data.adxInvoiceId})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: AdxInvoiceDetailResponse[]) => {
          if (data.length > 0) {
            this.setFormValues(data[0]);
            this.setFilteredOptions();
          }
        }
      });
  }

  private setFormValues(invoice: AdxInvoiceDetailResponse) {
    this.form.controls.adxInvoiceId.setValue(invoice.id);
    this.form.controls.recordLocator.setValue(invoice.recordLocator);
    this.form.controls.dataSourceId.setValue(invoice.tramsDataSource.id);
  }

  private setFilteredOptions() {
    const datasource = this.form.controls.dataSourceId.value;
    this.filteredVendorOptions$ = autocompleteFormFilter(
      this.vendorControl,
      'vendorName',
      replayHttpQuery(this.vendorService.getVendors(
        new HttpParams({fromObject: {IsDeleted: false, DataSource: datasource}})
      ))
    );

    this.filteredAgentOptions$ = autocompleteFormFilter(
      this.agentControl,
      'agentName',
      replayHttpQuery(this.agentService.getAllAgents(datasource))
    )
  }

  private subscribeToControls() {
    combineLatest({
      departDate: this.departDateControl.valueChanges.pipe(startWith(this.departDateControl.value)),
      returnDate: this.returnDateControl.valueChanges.pipe(startWith(this.returnDateControl.value))
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({departDate, returnDate}) => {
        if (!departDate || !returnDate) {
          const errors = this.returnDateControl.errors;
          if (!errors) return;
          const {invalidDate: _invalidDate, ...prevErrors} = errors;
          this.returnDateControl.setErrors(prevErrors);
          return;
        }
        if (departDate && returnDate && departDate > returnDate) {
          this.returnDateControl.setErrors({invalidDate: true});
        }
      });
  }

  protected displayVendorFn(vendor: VendorModel): string {
    return vendor && vendor.vendorName ? vendor.vendorName : '';
  }

  protected displayAgentFn(agent: Agent): string {
    return agent && agent.agentName ? agent.agentName : '';
  }

  protected preventDecimal(event: KeyboardEvent) {
    if (event.key === '.' || event.key === ',') {
      event.preventDefault();
    }
  }

  protected validateMaxDecimals(event: KeyboardEvent) {
    const key = event.key;
    const ALLOWED_KEYS = [
      'ArrowLeft',
      'ArrowRight',
      'Backspace',
      'Delete',
      'Tab',
      'Home',
      'End',
    ];

    if (ALLOWED_KEYS.includes(key) || event.ctrlKey || event.metaKey)
      return;

    const value = (event.target as HTMLInputElement).value;
    if (!value.includes('.')) return;
    const digits = value.split('.')[1];
    if (digits.length < 4) return;
    event.preventDefault();
  }

  protected resetFilter(control: keyof ServiceFormValues) {
    this.form.controls[control].reset();
  }

  protected close(update?: boolean) {
    this.dialogRef.close(update);
  }

  protected apply() {
    this.loading.set(true);
    const {interCode, vendor, agent, currency, ...rest} = this.form.getRawValue();
    const form = {
      ...rest,
      interCode: !interCode || interCode === '-1' ? null : interCode,
      vendorId: vendor?.id,
      agentId: agent?.id,
      currencyId: currency?.id,
      currencyCode: currency?.code,
    };
    this.serviceForm.create(form)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.snackBar.open('Successfully added new booking!', 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['message-position', 'app-notification-success']
          });
          this.close(true);
        },
        error: () => {
          this.loading.set(false);
          this.snackBar.open('Failed to add new booking.', 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['message-position', 'app-notification-error']
          });
        }
      });
  }

  protected onTravelTypeChange(travelTypeId: number): void {
    if (travelTypeId == 7) {
      this.form.get('externalTravelServiceTypeId')?.enable();
    }
    else{
      this.form.get('externalTravelServiceTypeId')?.disable();
      this.form.get('externalTravelServiceTypeId')?.reset();
    }
  }
}
